import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import {
  Plus, ArrowRight, MessageSquare, CheckCircle2, Circle,
  Zap, Clock, Loader2
} from 'lucide-react';
import { format, parseISO, formatDistanceToNow } from 'date-fns';

const TYPE_CONFIG = {
  created: { icon: Plus, color: 'bg-emerald-100 text-emerald-600', label: 'Created' },
  stage_change: { icon: ArrowRight, color: 'bg-orange-100 text-orange-600', label: 'Stage Change' },
  note: { icon: MessageSquare, color: 'bg-purple-100 text-purple-600', label: 'Note' },
  task_created: { icon: Circle, color: 'bg-blue-100 text-blue-600', label: 'Task Created' },
  task_completed: { icon: CheckCircle2, color: 'bg-green-100 text-green-600', label: 'Completed' },
  automation: { icon: Zap, color: 'bg-amber-100 text-amber-600', label: 'Automation' },
};

export default function LeadActivityTimeline({ leadId }) {
  const { token } = useAuth();
  const [timeline, setTimeline] = useState([]);
  const [leadName, setLeadName] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!leadId) return;
    setLoading(true);
    api.get(token, `/leads/${leadId}/timeline`)
      .then(res => {
        setTimeline(res.data.timeline || []);
        setLeadName(res.data.leadName || '');
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [leadId, token]);

  if (loading) {
    return (
      <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#4A3B32]" /></div>
    );
  }

  if (timeline.length === 0) {
    return (
      <div className="text-center py-8">
        <Clock className="w-10 h-10 text-[#D4CCC6] mx-auto mb-2" />
        <p className="text-sm text-[#5D524D]">No activity recorded for this lead</p>
      </div>
    );
  }

  return (
    <div className="space-y-1" data-testid="lead-activity-timeline">
      <div className="relative">
        <div className="absolute left-[15px] top-4 bottom-4 w-px bg-[#E6E0D9]" />

        <div className="space-y-3">
          {timeline.map((item, i) => {
            const config = TYPE_CONFIG[item.type] || TYPE_CONFIG.created;
            const IconComp = config.icon;
            const ts = item.timestamp || '';

            return (
              <div key={i} className="flex gap-3 relative" data-testid={`timeline-item-${i}`}>
                <div className={cn("w-8 h-8 rounded-full flex items-center justify-center shrink-0 z-10", config.color)}>
                  <IconComp className="w-3.5 h-3.5" />
                </div>
                <div className="flex-1 min-w-0 pt-0.5">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium text-[#2C2420]">{item.title}</p>
                    <Badge className={cn("text-[9px] shrink-0", config.color)}>{config.label}</Badge>
                  </div>
                  {item.description && (
                    <p className="text-xs text-[#5D524D] mt-0.5 line-clamp-2">{item.description}</p>
                  )}
                  <div className="flex items-center gap-2 mt-1">
                    {item.author && <span className="text-[10px] text-[#4A3B32] font-medium">{item.author}</span>}
                    <span className="text-[10px] text-[#8C8078]">
                      {ts ? formatDistanceToNow(parseISO(ts), { addSuffix: true }) : ''}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
